import { useRef, useState } from 'react';
import type { MouseEvent, ReactElement } from 'react';

import './donut-chart.scss';

export interface DonutChartRange {
  label: string;
  percentage: number;
  color: string;
  detail?: string;
}

interface DonutChartProps {
  ranges: DonutChartRange[];
  size?: number;
  strokeWidth?: number;
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  startAngle: number,
  endAngle: number,
): string {
  const o1 = polarToCartesian(cx, cy, outerR, startAngle);
  const o2 = polarToCartesian(cx, cy, outerR, endAngle);
  const i1 = polarToCartesian(cx, cy, innerR, endAngle);
  const i2 = polarToCartesian(cx, cy, innerR, startAngle);
  const large = endAngle - startAngle > 180 ? 1 : 0;
  return [
    `M ${o1.x} ${o1.y}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${o2.x} ${o2.y}`,
    `L ${i1.x} ${i1.y}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${i2.x} ${i2.y}`,
    'Z',
  ].join(' ');
}

export function DonutChart({
  ranges,
  size = 80,
  strokeWidth = 12,
}: DonutChartProps): ReactElement {
  const cx = size / 2;
  const cy = size / 2;
  const outerR = cx * 0.9;
  const innerR = outerR - strokeWidth;
  const hoverGrow = strokeWidth * 0.25;

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);

  const renderableRanges = ranges
    .map((range, index) => ({ range, index }))
    .filter(({ range }) => range.percentage > 0);

  const total = renderableRanges.reduce((s, { range }) => s + range.percentage, 0);

  const MIN_SWEEP_DEG = 8; // minimum arc size so tiny segments stay visible

  // First pass: compute raw sweeps and clamp to minimum
  const rawSweeps =
    total > 0
      ? renderableRanges.map(({ range }) => (range.percentage / total) * 360)
      : [];
  const clampedSweeps = rawSweeps.map((s) => Math.max(s, MIN_SWEEP_DEG));
  const clampedTotal = clampedSweeps.reduce((a, b) => a + b, 0);
  // Scale down so all sweeps still sum to 360°
  const normalizedSweeps =
    clampedTotal > 0 ? clampedSweeps.map((s) => (s / clampedTotal) * 360) : [];

  let cursor = 0;
  const segments = renderableRanges.map(({ range, index }, i) => {
    const sweep = normalizedSweeps.at(i) ?? 0;
    const start = cursor;
    const end = cursor + sweep - 0.5; // small gap between segments
    cursor += sweep;
    return { range, start, end, index };
  });

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (rect) {
      setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  };

  const hoveredRange = hoveredIndex !== null ? (ranges.at(hoveredIndex) ?? null) : null;

  function getDetailText(label: string): string {
    if (label === 'Unknown') return 'with no thresholds defined';
    return `in ${label.toLowerCase()} range`;
  }

  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', display: 'inline-block', width: size, height: size }}
      onMouseMove={handleMouseMove}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} overflow="visible">
        {segments.map(({ range, start, end, index }) => {
          const hovered = hoveredIndex === index;
          const currentOuterR = hovered ? outerR + hoverGrow : outerR;
          const currentInnerR = hovered ? innerR - hoverGrow * 0.5 : innerR;
          return (
            <path
              key={range.label}
              data-testid="donut-chart-segment"
              d={arcPath(cx, cy, currentOuterR, currentInnerR, start, end)}
              fill={range.color}
              stroke="white"
              strokeWidth={1}
              style={{ cursor: 'pointer', transition: 'd 0.15s ease' }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            />
          );
        })}
      </svg>

      {hoveredRange && (
        <div
          className="donut-chart__tooltip"
          style={{ left: tooltipPos.x, top: tooltipPos.y }}
        >
          <span className="donut-chart__tooltip-label">{hoveredRange.label}</span>
          <span className="donut-chart__tooltip-value">
            {hoveredRange.percentage}% {' · '}
            {hoveredRange.detail} {getDetailText(hoveredRange.label)}
          </span>
        </div>
      )}
    </div>
  );
}
